import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
} from '@simplewebauthn/browser';

/**
 * WebAuthn utilities for frontend biometric authentication
 */
class WebAuthnManager {
  constructor(api) {
    this.api = api;
  }

  /**
   * Check if WebAuthn is supported by the browser
   */
  isSupported() {
    return browserSupportsWebAuthn();
  }

  /**
   * Check if platform authenticator (FaceID/TouchID) is available
   */
  async isPlatformAuthenticatorAvailable() {
    if (!this.isSupported()) {
      return false;
    }
    
    try {
      return await platformAuthenticatorIsAvailable();
    } catch (error) {
      console.warn('[WEBAUTHN] Platform authenticator check failed:', error);
      return false;
    }
  }

  /**
   * Get a human-readable name for the current platform's biometric authentication
   */
  getBiometricName() {
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (userAgent.includes('iphone') || userAgent.includes('ipad')) {
      return 'Face ID или Touch ID';
    } else if (userAgent.includes('mac')) {
      return 'Touch ID';
    } else if (userAgent.includes('android')) {
      return 'Отпечаток пальца или Face Unlock';
    } else if (userAgent.includes('windows')) {
      return 'Windows Hello';
    } else {
      return 'Биометрическая аутентификация';
    }
  }

  /**
   * Register a new biometric authenticator for the current user
   */
  async registerBiometric(deviceName = null) {
    try {
      console.log('[WEBAUTHN] Starting biometric registration...');
      
      // Check if WebAuthn is supported
      if (!this.isSupported()) {
        throw new Error('WebAuthn не поддерживается этим браузером');
      }

      // Check if platform authenticator is available
      const isAvailable = await this.isPlatformAuthenticatorAvailable();
      if (!isAvailable) {
        throw new Error('Биометрическая аутентификация недоступна на этом устройстве');
      }

      // Get registration options from server
      console.log('[WEBAUTHN] Getting registration options...');
      const optionsResponse = await this.api.post('api/webauthn/register/generate');
      
      if (optionsResponse.error) {
        throw new Error(optionsResponse.error);
      }

      console.log('[WEBAUTHN] Starting registration ceremony...');
      
      // Start the WebAuthn registration ceremony
      const attResp = await startRegistration(optionsResponse);
      
      console.log('[WEBAUTHN] Registration response received, verifying...');
      
      // Send the response to the server for verification
      const verificationResponse = await this.api.post('api/webauthn/register/verify', {
        ...attResp,
        name: deviceName || this.getBiometricName()
      });
      
      if (verificationResponse.error) {
        throw new Error(verificationResponse.error);
      }

      if (!verificationResponse.verified) {
        throw new Error('Не удалось подтвердить регистрацию биометрических данных');
      }

      console.log('[WEBAUTHN] Biometric registration successful!');
      return verificationResponse;
      
    } catch (error) {
      console.error('[WEBAUTHN] Registration failed:', error);
      
      // Provide user-friendly error messages
      if (error.name === 'InvalidStateError') {
        throw new Error('Это устройство уже зарегистрировано для биометрической аутентификации');
      } else if (error.name === 'NotAllowedError') {
        throw new Error('Регистрация биометрических данных была отменена или заблокирована');
      } else if (error.name === 'NotSupportedError') {
        throw new Error('Биометрическая аутентификация не поддерживается на этом устройстве');
      } else if (error.name === 'SecurityError') {
        throw new Error('Ошибка безопасности: убедитесь, что сайт открыт по HTTPS или localhost');
      } else if (error.name === 'AbortError') {
        throw new Error('Операция была прервана');
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Не удалось зарегистрировать биометрические данные');
      }
    }
  }

  /**
   * Authenticate using biometric authenticator
   */
  async authenticateWithBiometric(phone) {
    try {
      console.log('[WEBAUTHN] Starting biometric authentication...');
      
      // Check if WebAuthn is supported
      if (!this.isSupported()) {
        throw new Error('WebAuthn не поддерживается этим браузером');
      }

      // Get authentication options from server
      console.log('[WEBAUTHN] Getting authentication options...');
      const optionsResponse = await this.api.post('api/webauthn/authenticate/generate', {
        phone
      });
      
      if (optionsResponse.error) {
        throw new Error(optionsResponse.error);
      }

      console.log('[WEBAUTHN] Starting authentication ceremony...');
      
      // Start the WebAuthn authentication ceremony
      const authResp = await startAuthentication(optionsResponse);
      
      console.log('[WEBAUTHN] Authentication response received, verifying...');
      
      // Send the response to the server for verification
      const verificationResponse = await this.api.post('api/webauthn/authenticate/verify', {
        body: authResp,
        phone
      });
      
      if (verificationResponse.error) {
        throw new Error(verificationResponse.error);
      }

      if (!verificationResponse.verified) {
        throw new Error('Не удалось подтвердить биометрическую аутентификацию');
      }

      console.log('[WEBAUTHN] Biometric authentication successful!');
      return verificationResponse;
      
    } catch (error) {
      console.error('[WEBAUTHN] Authentication failed:', error);
      
      // Provide user-friendly error messages
      if (error.name === 'InvalidStateError') {
        throw new Error('Биометрические данные недоступны');
      } else if (error.name === 'NotAllowedError') {
        throw new Error('Биометрическая аутентификация была отменена или заблокирована');
      } else if (error.name === 'NotSupportedError') {
        throw new Error('Биометрическая аутентификация не поддерживается на этом устройстве');
      } else if (error.name === 'SecurityError') {
        throw new Error('Ошибка безопасности при биометрической аутентификации');
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Не удалось выполнить биометрическую аутентификацию');
      }
    }
  }

  /**
   * Get user's registered authenticators
   */
  async getRegisteredAuthenticators() {
    try {
      const response = await this.api.get('api/webauthn/authenticators');
      if (response.error) {
        throw new Error(response.error);
      }
      return response.authenticators || [];
    } catch (error) {
      console.error('[WEBAUTHN] Failed to get authenticators:', error);
      throw new Error('Не удалось получить список зарегистрированных устройств');
    }
  }

  /**
   * Remove a registered authenticator
   */
  async removeAuthenticator(authenticatorId) {
    try {
      const response = await this.api.delete(`api/webauthn/authenticators/${authenticatorId}`);
      if (response.error) {
        throw new Error(response.error);
      }
      return response;
    } catch (error) {
      console.error('[WEBAUTHN] Failed to remove authenticator:', error);
      throw new Error('Не удалось удалить устройство');
    }
  }

  /**
   * Check if user has any registered biometric authenticators
   */
  async hasRegisteredBiometrics() {
    try {
      // First try the authenticated endpoint (for logged in users)
      const authenticators = await this.getRegisteredAuthenticators();
      return authenticators.length > 0;
    } catch (error) {
      console.warn('[WEBAUTHN] Could not check for registered biometrics via authenticated endpoint:', error);
      // Return false for now - in login page we use a different approach
      return false;
    }
  }
}

// Create and export a factory function to create WebAuthn manager instances
export function createWebAuthnManager(api) {
  return new WebAuthnManager(api);
}

// Export the class as well for direct usage
export { WebAuthnManager };
